import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    fetchSurveys,
    restoreSurveyArchive,
} from '../utils/explorerApi';
import {
    archiveWithOverwriteCheck,
    fetchAllDeployments,
    fetchArchiveStatus,
    fetchDeploymentStats,
    fetchArchiveHistory,
    updateWorkspaceArchiveNotes,
} from '../utils/archiveApi';
import { showToast } from './Toast';

/**
 * ArchiverPanel — the Explorer's contextual archive action
 * (archive_design.md, Version 4; results_archiver_design.md for the status
 * strip + provenance history).
 *
 * The Archiver is a menu-bar tool that opens with no implicit binding to
 * Explorer's current selection. What it does:
 *
 *   1. Show, across the top, where results will go: a status strip fed by the
 *      archive server's status endpoint — the open archive file's name and a
 *      summary of the last merge, or a quiet nudge to open one in the Console.
 *   2. List the org's deployments with their content (sessions, responses,
 *      audio, attachments) and whether each has already been archived. A
 *      deployment's RESULTS are the artifact the Explorer archives; open
 *      deployments are visible but disabled.
 *   3. Submit the selection to the archive server, which merges it into the
 *      account's one canonical singleton archive. Copy-only: nothing is ever
 *      deleted from the live system.
 *   4. History tab: only real merges (operations that wrote provenance rows),
 *      newest per deployment, with per-row restore-from-file.
 */
export default function ArchiverPanel({ onClose }) {
    // -- tabs ---------------------------------------------------------------
    const [tab, setTab] = useState('new'); // 'new' | 'history'

    // -- "archive results" tab state ----------------------------------------
    const [deployments, setDeployments] = useState([]); // + survey_name, stats joined
    const [deploymentsLoading, setDeploymentsLoading] = useState(false);
    const [selectedDeploymentIds, setSelectedDeploymentIds] = useState(new Set());
    const [archiveRunning, setArchiveRunning] = useState(false);
    const [archiveProgress, setArchiveProgress] = useState('');
    // Canonical archive status: null = loading, false = no archive open,
    // object = { open, file_name, adopted_at, last_merge, totals }.
    const [archiveStatus, setArchiveStatus] = useState(null);
    // Overwrite consent dialog: { collisions, resolve } | null
    const [overwriteModal, setOverwriteModal] = useState(null);

    // -- "history" tab state -----------------------------------------------
    const [archives, setArchives] = useState([]);
    const [archivesLoading, setArchivesLoading] = useState(false);
    const [historyFilter, setHistoryFilter] = useState('');
    const [historySort, setHistorySort] = useState({ key: 'archived_at', dir: 'desc' });
    const [selectedRecordId, setSelectedRecordId] = useState(null);

    // -- modal state -------------------------------------------------------
    // null | 'notes' | 'mismatch'
    const [modal, setModal] = useState(null);
    const [notesDraft, setNotesDraft] = useState('');
    const [notesSaving, setNotesSaving] = useState(false);
    const [mismatchMsg, setMismatchMsg] = useState('');

    // -- per-row restore state ---------------------------------------------
    const [restoreRow, setRestoreRow] = useState(null); // the history row being restored
    const [restoreRunning, setRestoreRunning] = useState(false);
    const rowFileInputRef = useRef(null);

    const [error, setError] = useState('');

    // ---------------------------------------------------------------------
    // Loaders
    // ---------------------------------------------------------------------
    const loadDeployments = useCallback(async () => {
        setDeploymentsLoading(true);
        setError('');
        try {
            const [deps, surveys, stats] = await Promise.all([
                fetchAllDeployments(),
                fetchSurveys().catch(() => []),
                fetchDeploymentStats().catch(() => []),
            ]);
            const nameBySurvey = {};
            (surveys || []).forEach(s => {
                const id = s.survey_id || s.id;
                if (id) nameBySurvey[id] = s.name || s.title || id;
            });
            const statById = {};
            (stats || []).forEach(st => { if (st && st.deployment_id) statById[st.deployment_id] = st; });
            setDeployments((deps || []).map(d => ({
                ...d,
                survey_name: nameBySurvey[d.survey_id] || d.survey_id,
                stats: statById[d.deployment_id] || null,
            })));
        } catch (err) {
            setError(err.message || 'Failed to load deployments');
        } finally {
            setDeploymentsLoading(false);
        }
    }, []);

    // Canonical archive status — is there an open archive to submit into, and
    // what does it currently hold?
    const loadArchiveStatus = useCallback(async () => {
        try {
            const st = await fetchArchiveStatus();
            setArchiveStatus(st && st.open ? st : false);
        } catch (_) {
            setArchiveStatus(false);
        }
    }, []);

    const loadArchives = useCallback(async () => {
        setArchivesLoading(true);
        try {
            const data = await fetchArchiveHistory(); // account-wide, real merges only
            setArchives(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message || 'Failed to load archive history');
        } finally {
            setArchivesLoading(false);
        }
    }, []);

    useEffect(() => { loadDeployments(); }, [loadDeployments]);
    useEffect(() => { loadArchives(); }, [loadArchives]);
    useEffect(() => { loadArchiveStatus(); }, [loadArchiveStatus]);

    // The archive server broadcasts merge/adopt over SSE, but that stream does
    // not survive the Explorer's request/response proxy cleanly. Poll the
    // (cheap, single-row) status endpoint so a change made in the Console —
    // opening a different file, a merge from another tab — surfaces here on its
    // own, without a manual refresh. Local actions refetch immediately below.
    useEffect(() => {
        const id = setInterval(() => { loadArchiveStatus(); }, 12000);
        return () => clearInterval(id);
    }, [loadArchiveStatus]);

    // ---------------------------------------------------------------------
    // Selection — the artifact is a deployment's results
    // ---------------------------------------------------------------------
    const toggleDeploymentSelection = (id) => {
        setSelectedDeploymentIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const archivableOnly = useMemo(
        () => deployments.filter(d => !d.is_open),
        [deployments]
    );

    const toggleSelectAll = () => {
        if (selectedDeploymentIds.size === archivableOnly.length && archivableOnly.length > 0) {
            setSelectedDeploymentIds(new Set());
        } else {
            setSelectedDeploymentIds(new Set(archivableOnly.map(d => d.deployment_id)));
        }
    };

    // ---------------------------------------------------------------------
    // Archive results — one batched submission to the archive server.
    // ---------------------------------------------------------------------
    const confirmOverwrite = (collisions) => new Promise((resolve) => {
        setOverwriteModal({ collisions, resolve });
    });

    const handleArchiveResults = async () => {
        if (selectedDeploymentIds.size === 0 || archiveRunning) return;
        const ids = archivableOnly
            .filter(d => selectedDeploymentIds.has(d.deployment_id))
            .map(d => d.deployment_id);
        if (ids.length === 0) return;

        setArchiveRunning(true);
        setError('');
        try {
            const result = await archiveWithOverwriteCheck(
                { deployment_ids: ids },
                (p) => setArchiveProgress(
                    p.stage === 'merging'
                        ? `Archiving results of ${ids.length} deployment${ids.length === 1 ? '' : 's'}…`
                        : ''
                ),
                { confirmOverwrite }
            );
            setSelectedDeploymentIds(new Set());
            await Promise.all([loadArchives(), loadArchiveStatus(), loadDeployments()]);

            const deplLabel = `${ids.length} deployment${ids.length === 1 ? '' : 's'}`;
            const sessionCount = Number(result?.session_count || 0);
            const responseCount = Number(result?.response_count || 0);
            // Explicit false only: an older server omits the field, and a missing
            // field must not raise a false alarm.
            const provenanceFailed = result?.provenance_logged === false;
            const historyCaveat = provenanceFailed
                ? ' …but the archive history could not be updated — this merge may not appear in History.'
                : '';
            if (sessionCount === 0 && responseCount === 0) {
                showToast(
                    `Archived ${deplLabel}, but no response data was found — nothing to analyze was written. This deployment has no completed sessions.`,
                    'warning'
                );
            } else {
                showToast(
                    `Archived results of ${deplLabel} — ${responseCount} response${responseCount === 1 ? '' : 's'} across ${sessionCount} session${sessionCount === 1 ? '' : 's'}. The open archive file is updated by the console.${historyCaveat}`,
                    // The archive succeeded; a provenance-log miss is a warning, not an error.
                    provenanceFailed ? 'warning' : 'success'
                );
            }
        } catch (err) {
            if (err.canceled) {
                // Author declined the replace — not an error.
            } else if (err.status === 404 || err.code === 'NO_ARCHIVE') {
                setArchiveStatus(false);
                setError('No archive is open for this account. Open or create one in the Console under Settings → Archives, then archive from here.');
            } else {
                setError(`Archive failed: ${err.message}`);
            }
        } finally {
            setArchiveRunning(false);
            setArchiveProgress('');
        }
    };

    // ---------------------------------------------------------------------
    // History tab
    // ---------------------------------------------------------------------
    const historyFiltered = useMemo(() => {
        const q = historyFilter.trim().toLowerCase();
        const filtered = q
            ? archives.filter(a =>
                (a.survey_name || '').toLowerCase().includes(q) ||
                (a.file_name || '').toLowerCase().includes(q) ||
                (a.notes || '').toLowerCase().includes(q))
            : archives.slice();
        const dir = historySort.dir === 'asc' ? 1 : -1;
        const key = historySort.key;
        filtered.sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (av === bv) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            return av > bv ? dir : -dir;
        });
        return filtered;
    }, [archives, historyFilter, historySort]);

    const toggleHistorySort = (key) => {
        setHistorySort(prev => prev.key === key
            ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: key === 'archived_at' ? 'desc' : 'asc' });
    };

    const selectedArchive = useMemo(
        () => archives.find(a => a.archive_record_id === selectedRecordId) || null,
        [archives, selectedRecordId]
    );

    // -- Edit Notes modal --------------------------------------------------
    const openEditNotes = () => {
        if (!selectedArchive) return;
        setNotesDraft(selectedArchive.notes || '');
        setModal('notes');
    };

    const saveNotes = async () => {
        if (!selectedArchive) return;
        setNotesSaving(true);
        try {
            await updateWorkspaceArchiveNotes(selectedArchive.archive_record_id, notesDraft);
            await loadArchives();
            setModal(null);
        } catch (err) {
            showToast(`Failed to save notes: ${err.message}`, 'error');
        } finally {
            setNotesSaving(false);
        }
    };

    // -- Per-row restore-from-file ----------------------------------------
    // The author's mental model is "here's the archive operation for my survey;
    // hand me the file and I'll bring it back". Pick a file, verify its SHA-256
    // against the row's stored checksum silently, then restore. No checksum is
    // ever shown; a mismatch surfaces as a styled modal, not a native alert.
    const startRowRestore = (row) => {
        if (restoreRunning) return;
        setRestoreRow(row);
        // Defer the click so restoreRow is set before onChange fires.
        setTimeout(() => rowFileInputRef.current && rowFileInputRef.current.click(), 0);
    };

    const onPickRowRestoreFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = ''; // allow re-picking the same file
        const row = restoreRow;
        if (!file || !row) { setRestoreRow(null); return; }
        setRestoreRunning(true);
        try {
            const buf = await file.arrayBuffer();
            const hashBuf = await crypto.subtle.digest('SHA-256', buf);
            const hash = Array.from(new Uint8Array(hashBuf))
                .map(b => b.toString(16).padStart(2, '0'))
                .join('');
            if (row.manifest_checksum && row.manifest_checksum.toLowerCase() !== hash) {
                setMismatchMsg(
                    `That file does not match this archive record (${row.survey_name || 'survey'}, ` +
                    `${formatDate(row.archived_at)}). It may be a different archive, a different ` +
                    `version, or have been altered. Pick the file this row was archived into.`
                );
                setModal('mismatch');
                return;
            }
            const result = await restoreSurveyArchive(file);
            showToast(`Survey "${result.survey_name}" restored successfully`, 'success');
            await Promise.all([loadDeployments(), loadArchives()]);
        } catch (err) {
            if (err.status === 403) {
                showToast('Upgrade required: restoring archives is available on paid plans.', 'error');
            } else {
                showToast(err.message || 'Restore failed.', 'error');
            }
        } finally {
            setRestoreRunning(false);
            setRestoreRow(null);
        }
    };

    // ---------------------------------------------------------------------
    // Helpers
    // ---------------------------------------------------------------------
    const formatBytes = (bytes) => {
        if (!bytes) return '—';
        const units = ['B', 'KB', 'MB', 'GB'];
        const k = 1024;
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${(bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
    };

    const sortArrow = (key) =>
        key !== historySort.key ? '' : (historySort.dir === 'asc' ? ' ▲' : ' ▼');

    // ---------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------
    return (
        <div style={styles.root}>
            {/* Hidden file input drives every per-row restore. */}
            <input
                ref={rowFileInputRef}
                type="file"
                accept=".samarchive,.zip"
                style={{ display: 'none' }}
                onChange={onPickRowRestoreFile}
            />

            {/* Header */}
            <div style={styles.header}>
                <span style={styles.titleText}>Results Archiver</span>
                <div style={styles.tabs}>
                    <button
                        type="button"
                        style={{ ...styles.tab, ...(tab === 'new' ? styles.tabActive : {}) }}
                        onClick={() => setTab('new')}
                    >
                        Archive Results
                        {archivableOnly.length > 0 && (
                            <span style={styles.tabCount}>{archivableOnly.length}</span>
                        )}
                    </button>
                    <button
                        type="button"
                        style={{ ...styles.tab, ...(tab === 'history' ? styles.tabActive : {}) }}
                        onClick={() => setTab('history')}
                    >
                        History
                        {archives.length > 0 && (
                            <span style={styles.tabCount}>{archives.length}</span>
                        )}
                    </button>
                </div>
                <div style={{ flex: 1 }} />
            </div>

            {/* Always-visible status strip: where results will go. */}
            <ArchiveStatusStrip status={archiveStatus} />

            {/* Progress / error strip */}
            {(error || archiveProgress) && (
                <div style={{ ...styles.statusStrip, ...(error ? styles.statusError : styles.statusInfo) }}>
                    {error || archiveProgress}
                </div>
            )}

            {/* Body */}
            {tab === 'new' ? (
                <ResultsArchiveView
                    deployments={deployments}
                    loading={deploymentsLoading}
                    selected={selectedDeploymentIds}
                    onToggle={toggleDeploymentSelection}
                    onToggleAll={toggleSelectAll}
                    onRefresh={() => { loadDeployments(); loadArchiveStatus(); }}
                    archiveRunning={archiveRunning}
                    onArchive={handleArchiveResults}
                    archiveOpen={archiveStatus !== false && archiveStatus !== null}
                    formatBytes={formatBytes}
                />
            ) : (
                <HistoryView
                    archives={historyFiltered}
                    rawCount={archives.length}
                    loading={archivesLoading}
                    filter={historyFilter}
                    onFilter={setHistoryFilter}
                    sort={historySort}
                    toggleSort={toggleHistorySort}
                    selectedId={selectedRecordId}
                    onSelect={setSelectedRecordId}
                    onEditNotes={openEditNotes}
                    onRefresh={loadArchives}
                    onRestoreRow={startRowRestore}
                    restoreRunning={restoreRunning}
                    restoreRowId={restoreRow?.archive_record_id || null}
                    formatBytes={formatBytes}
                    sortArrow={sortArrow}
                />
            )}

            {/* Restore mismatch modal */}
            {modal === 'mismatch' && (
                <ModalShell title="File does not match" onClose={() => setModal(null)} width={480}>
                    <div style={styles.modalIntro}>{mismatchMsg}</div>
                    <div style={styles.modalActions}>
                        <button type="button" style={{ ...styles.cmdBtn, ...styles.cmdBtnPrimary }} onClick={() => setModal(null)}>
                            OK
                        </button>
                    </div>
                </ModalShell>
            )}

            {/* Overwrite consent modal — whole-instance replace in the archive */}
            {overwriteModal && (
                <ModalShell
                    title="Replace archived results?"
                    onClose={() => { overwriteModal.resolve(false); setOverwriteModal(null); }}
                    width={520}
                >
                    <div style={styles.modalIntro}>
                        The archive already holds results for
                        {overwriteModal.collisions.length === 1 ? ' this deployment' : ' these deployments'}.
                        Archiving again replaces the archived copy — the previous archived
                        version cannot be recovered except from an older archive file you
                        may have kept. Your live data is not affected.
                    </div>
                    <ul style={{ margin: '4px 0 12px 18px', padding: 0, fontSize: 12 }}>
                        {overwriteModal.collisions.map(c => (
                            <li key={c.id} style={{ marginBottom: 2 }}>{c.name}</li>
                        ))}
                    </ul>
                    <div style={styles.modalActions}>
                        <button
                            type="button"
                            style={styles.cmdBtn}
                            onClick={() => { overwriteModal.resolve(false); setOverwriteModal(null); }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            style={{ ...styles.cmdBtn, ...styles.cmdBtnPrimary }}
                            onClick={() => { overwriteModal.resolve(true); setOverwriteModal(null); }}
                        >
                            Replace
                        </button>
                    </div>
                </ModalShell>
            )}

            {/* Edit Notes modal */}
            {modal === 'notes' && selectedArchive && (
                <ModalShell title="Edit Archive Notes" onClose={() => setModal(null)} width={520}>
                    <div style={styles.modalIntro}>
                        Notes for archive from {formatDate(selectedArchive.archived_at)}
                        {' '}— survey "{selectedArchive.survey_name || 'survey'}".
                    </div>
                    <textarea
                        autoFocus
                        rows={5}
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        placeholder="What is this archive? Why was it taken? Where is the .samarchive file stored?"
                        style={styles.notesTextarea}
                    />
                    <div style={styles.modalActions}>
                        <button type="button" style={styles.cmdBtn} onClick={() => setModal(null)} disabled={notesSaving}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            style={{ ...styles.cmdBtn, ...styles.cmdBtnPrimary }}
                            onClick={saveNotes}
                            disabled={notesSaving}
                        >
                            {notesSaving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </ModalShell>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Status strip — where results will go. Quiet when no archive is open (a
// pointer, not an alarm), informative when one is.
// ---------------------------------------------------------------------------
function ArchiveStatusStrip({ status }) {
    if (status === null) {
        return <div style={{ ...styles.statusStrip, ...styles.statusNeutral }}>Checking archive status…</div>;
    }
    if (status === false) {
        return (
            <div style={{ ...styles.statusStrip, ...styles.statusNeutral }}>
                No archive file is open. Open or create one in the Console under Settings → Archives.
            </div>
        );
    }
    const lm = status.last_merge;
    let summary = '';
    if (lm) {
        const parts = [];
        if (lm.survey_name) parts.push(lm.survey_name);
        parts.push(`${lm.sessions || 0} session${(lm.sessions || 0) === 1 ? '' : 's'}`);
        parts.push(`${lm.responses || 0} response${(lm.responses || 0) === 1 ? '' : 's'}`);
        summary = ` — last updated ${formatDate(lm.at)} (${parts.join(', ')})`;
    }
    return (
        <div style={{ ...styles.statusStrip, ...styles.statusInfo }}>
            Archiving into <strong>{status.file_name || 'the open archive'}</strong>{summary}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sub-views
// ---------------------------------------------------------------------------

function ResultsArchiveView({
    deployments, loading, selected, onToggle, onToggleAll, onRefresh,
    archiveRunning, onArchive, archiveOpen, formatBytes,
}) {
    const archivableCount = deployments.filter(d => !d.is_open).length;
    const allSelected = archivableCount > 0 && selected.size === archivableCount;

    return (
        <>
            <div style={styles.commandBar}>
                <button
                    type="button"
                    style={{
                        ...styles.cmdBtn,
                        ...styles.cmdBtnPrimary,
                        ...(selected.size === 0 || archiveRunning || !archiveOpen ? styles.cmdBtnDisabled : {}),
                    }}
                    disabled={selected.size === 0 || archiveRunning || !archiveOpen}
                    onClick={onArchive}
                    title={
                        !archiveOpen
                            ? 'Open an archive file in the Console (Settings → Archives) first'
                            : selected.size === 0
                                ? 'Select one or more closed deployments'
                                : `Archive results of ${selected.size} selected deployment${selected.size === 1 ? '' : 's'}`
                    }
                >
                    {archiveRunning ? 'Archiving…' : `Archive Results${selected.size > 0 ? ` (${selected.size})` : ''}`}
                </button>
                <button type="button" style={styles.cmdBtn} onClick={onRefresh} disabled={loading || archiveRunning}>
                    Refresh
                </button>
                <div style={{ flex: 1 }} />
                {archivableCount > 0 && (
                    <span style={styles.archivableCountLabel}>
                        {archivableCount} archivable
                        {deployments.length > archivableCount && (
                            <span style={styles.archivableMuted}>
                                {' '}— {deployments.length - archivableCount} still open
                            </span>
                        )}
                    </span>
                )}
            </div>

            <div style={styles.tableContainer}>
                {loading && deployments.length === 0 ? (
                    <div style={styles.placeholder}>Loading deployments…</div>
                ) : deployments.length === 0 ? (
                    <div style={styles.placeholder}>
                        No deployments yet. A deployment's results become archivable once
                        the deployment is closed; open deployments are still collecting
                        responses and cannot be archived until they are closed.
                    </div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, width: 28 }}>
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={onToggleAll}
                                        disabled={archivableCount === 0}
                                        aria-label="Select all archivable deployments"
                                    />
                                </th>
                                <th style={styles.th}>Survey</th>
                                <th style={styles.th}>Deployment</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>Sessions</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>Responses</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>Audio</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>Attachments</th>
                                <th style={styles.th}>Archived</th>
                                <th style={styles.th}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {deployments.map(d => {
                                const isSelected = selected.has(d.deployment_id);
                                const enabled = !d.is_open && !archiveRunning;
                                const st = d.stats;
                                return (
                                    <tr
                                        key={d.deployment_id}
                                        style={{ ...styles.row, ...(isSelected ? styles.rowSelected : {}), ...(d.is_open ? styles.rowDisabled : {}) }}
                                        onClick={() => enabled && onToggle(d.deployment_id)}
                                        title={d.is_open ? 'Undeploy this deployment first — its results can be archived once it is closed.' : undefined}
                                    >
                                        <td style={styles.td}>
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                disabled={!enabled}
                                                onChange={() => enabled && onToggle(d.deployment_id)}
                                                onClick={(e) => e.stopPropagation()}
                                                aria-label={`Select deployment ${d.deploy_slug || d.deployment_id}`}
                                            />
                                        </td>
                                        <td style={styles.td}>
                                            <span style={styles.surveyName}>{d.survey_name}</span>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={styles.idChip} title={d.deployment_id}>
                                                {d.deploy_slug || d.deployment_id.slice(0, 12)}
                                            </span>
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>{st ? st.session_count : '—'}</td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>{st ? st.response_count : '—'}</td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>{st ? (st.audio_count || 0) : '—'}</td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>
                                            {st
                                                ? (st.attachment_count > 0
                                                    ? `${st.attachment_count} (${formatBytes(st.attachment_bytes)})`
                                                    : '0')
                                                : '—'}
                                        </td>
                                        <td style={styles.td}>
                                            {st && st.archived
                                                ? (
                                                    <span title={`${st.archived.file_name} — ${formatDate(st.archived.archived_at)}`}>
                                                        <span style={styles.archivedName}>{st.archived.file_name}</span>
                                                        <span style={styles.archivedDate}> {formatDate(st.archived.archived_at)}</span>
                                                    </span>
                                                )
                                                : <span style={styles.notesEmpty}>—</span>}
                                        </td>
                                        <td style={styles.td}>
                                            {d.is_open
                                                ? <span style={styles.statusBlocked} title="Undeploy first to archive results">Open</span>
                                                : <span style={styles.statusReady}>Closed</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );
}

function HistoryView({
    archives, rawCount, loading, filter, onFilter, sort, toggleSort,
    selectedId, onSelect, onEditNotes, onRefresh, onRestoreRow,
    restoreRunning, restoreRowId, formatBytes, sortArrow,
}) {
    return (
        <>
            <div style={styles.commandBar}>
                <button
                    type="button"
                    style={styles.cmdBtn}
                    onClick={onEditNotes}
                    disabled={!selectedId}
                    title={selectedId ? 'Edit notes for the selected archive' : 'Select an archive first'}
                >
                    Edit Notes
                </button>
                <button type="button" style={styles.cmdBtn} onClick={onRefresh} disabled={loading}>
                    Refresh
                </button>
                <div style={{ flex: 1 }} />
                <input
                    type="text"
                    placeholder="Search survey, file, notes…"
                    value={filter}
                    onChange={(e) => onFilter(e.target.value)}
                    style={styles.searchInput}
                />
            </div>

            <div style={styles.tableContainer}>
                {loading && archives.length === 0 ? (
                    <div style={styles.placeholder}>Loading archive history…</div>
                ) : archives.length === 0 ? (
                    <div style={styles.placeholder}>
                        {rawCount === 0
                            ? 'No archives yet. Archive a deployment from the Archive Results tab; it appears here once its results land in a file.'
                            : 'No archives match the current search.'}
                    </div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, width: 28 }} aria-hidden="true"></th>
                                <th style={styles.th} onClick={() => toggleSort('archived_at')}>Archived{sortArrow('archived_at')}</th>
                                <th style={styles.th} onClick={() => toggleSort('file_name')}>File{sortArrow('file_name')}</th>
                                <th style={styles.th} onClick={() => toggleSort('survey_name')}>Survey{sortArrow('survey_name')}</th>
                                <th style={styles.th} onClick={() => toggleSort('scope')}>Scope{sortArrow('scope')}</th>
                                <th style={{ ...styles.th, textAlign: 'right' }} onClick={() => toggleSort('sessions')}>Sessions{sortArrow('sessions')}</th>
                                <th style={{ ...styles.th, textAlign: 'right' }} onClick={() => toggleSort('responses')}>Responses{sortArrow('responses')}</th>
                                <th style={{ ...styles.th, textAlign: 'right' }} onClick={() => toggleSort('attachments')}>Attachments{sortArrow('attachments')}</th>
                                <th style={{ ...styles.th, textAlign: 'right' }} onClick={() => toggleSort('audio_bytes')}>Audio{sortArrow('audio_bytes')}</th>
                                <th style={styles.th}>Notes</th>
                                <th style={styles.th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {archives.map(arch => {
                                const isSelected = arch.archive_record_id === selectedId;
                                const rowRestoring = restoreRunning && restoreRowId === arch.archive_record_id;
                                return (
                                    <tr
                                        key={arch.archive_record_id}
                                        style={{ ...styles.row, ...(isSelected ? styles.rowSelected : {}) }}
                                        onClick={() => onSelect(arch.archive_record_id)}
                                    >
                                        <td style={styles.td}>
                                            <input
                                                type="radio"
                                                checked={isSelected}
                                                onChange={() => onSelect(arch.archive_record_id)}
                                                aria-label="Select archive"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </td>
                                        <td style={styles.td}>{formatDate(arch.archived_at)}</td>
                                        <td style={styles.td}>
                                            <span style={styles.fileName} title={arch.file_name}>{arch.file_name || '—'}</span>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={styles.surveyName}>{arch.survey_name || 'survey'}</span>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={styles.scopeBadge}>{arch.scope || 'survey'}</span>
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>{arch.sessions ?? '—'}</td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>{arch.responses ?? '—'}</td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>{arch.attachments ?? '—'}</td>
                                        <td style={{ ...styles.td, textAlign: 'right' }}>{formatBytes(arch.audio_bytes)}</td>
                                        <td style={styles.td}>
                                            <span style={styles.notesCell} title={arch.notes || ''}>
                                                {arch.notes || <span style={styles.notesEmpty}>—</span>}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <button
                                                type="button"
                                                style={{ ...styles.cmdBtn, ...styles.cmdBtnSmall, ...(rowRestoring ? styles.cmdBtnDisabled : {}) }}
                                                disabled={restoreRunning}
                                                onClick={(e) => { e.stopPropagation(); onRestoreRow(arch); }}
                                                title="Pick this archive's .samarchive file to restore its results"
                                            >
                                                {rowRestoring ? 'Restoring…' : 'Restore from file…'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );
}

// ---------------------------------------------------------------------------
// Small modal shell — local to this file; keeps the dependency surface narrow.
// ---------------------------------------------------------------------------
function ModalShell({ title, onClose, width = 480, children }) {
    return (
        <div style={modalStyles.overlay} onClick={onClose}>
            <div
                style={{ ...modalStyles.dialog, width }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={modalStyles.header}>
                    <span style={modalStyles.title}>{title}</span>
                    <button type="button" style={modalStyles.close} onClick={onClose} aria-label="Close">
                        ✕
                    </button>
                </div>
                <div style={modalStyles.body}>{children}</div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDate(iso) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch (_) { return iso; }
}

// ---------------------------------------------------------------------------
// Styles — modeled on RecipientBrowser (canonical list pattern).
// ---------------------------------------------------------------------------
const styles = {
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: '#ffffff',
        color: '#3C4043',
        fontFamily: 'inherit',
        fontSize: 13,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '10px 16px',
        borderBottom: '1px solid #E8EAED',
        flexShrink: 0,
    },
    titleText: {
        fontWeight: 600,
        fontSize: 14,
        color: '#1F2937',
    },
    tabs: {
        display: 'flex',
        gap: 4,
    },
    tab: {
        background: 'transparent',
        border: '1px solid transparent',
        padding: '4px 10px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 500,
        color: '#5F6368',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    tabActive: {
        background: '#EBF4FF',
        color: '#1967D2',
        borderColor: '#BFDBFE',
    },
    tabCount: {
        background: '#ffffff',
        color: '#1F2937',
        border: '1px solid #DADCE0',
        borderRadius: 10,
        padding: '0 6px',
        fontSize: 10,
        fontWeight: 600,
        minWidth: 18,
        textAlign: 'center',
    },
    commandBar: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        padding: '8px 16px',
        borderBottom: '1px solid #F1F3F4',
        flexShrink: 0,
        flexWrap: 'wrap',
    },
    cmdBtn: {
        padding: '6px 12px',
        borderRadius: 6,
        border: '1px solid #DADCE0',
        background: '#ffffff',
        color: '#3C4043',
        fontSize: 12,
        fontWeight: 500,
        cursor: 'pointer',
    },
    cmdBtnSmall: {
        padding: '3px 8px',
        fontSize: 11,
    },
    cmdBtnPrimary: {
        background: '#5B9FFF',
        color: '#ffffff',
        border: '1px solid #5B9FFF',
    },
    cmdBtnDisabled: {
        background: '#cbd5e1',
        borderColor: '#cbd5e1',
        color: '#ffffff',
        cursor: 'not-allowed',
        opacity: 0.8,
    },
    archivableCountLabel: {
        fontSize: 12,
        color: '#137333',
        fontWeight: 600,
    },
    archivableMuted: {
        color: '#5F6368',
        fontWeight: 500,
    },
    searchInput: {
        flex: '0 1 280px',
        minWidth: 180,
        padding: '6px 10px',
        borderRadius: 6,
        border: '1px solid #DADCE0',
        fontSize: 13,
        outline: 'none',
    },
    statusStrip: {
        padding: '6px 16px',
        fontSize: 12,
        borderBottom: '1px solid #F1F3F4',
        flexShrink: 0,
    },
    statusInfo: {
        background: '#EFF6FF',
        color: '#1E3A8A',
    },
    // Neutral (no archive open): a pointer, not an alarm — deliberately gray.
    statusNeutral: {
        background: '#F1F3F4',
        color: '#5F6368',
    },
    statusError: {
        background: '#FEF2F2',
        color: '#991B1B',
    },
    tableContainer: {
        flex: 1,
        overflow: 'auto',
        padding: '0 16px',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 12,
    },
    th: {
        padding: '8px 6px',
        textAlign: 'left',
        borderBottom: '2px solid #E8EAED',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: 11,
        color: '#3C4043',
        position: 'sticky',
        top: 0,
        background: '#ffffff',
        userSelect: 'none',
        whiteSpace: 'nowrap',
    },
    td: {
        padding: '6px 6px',
        borderBottom: '1px solid #F5F6F7',
        verticalAlign: 'middle',
    },
    row: {
        cursor: 'pointer',
        transition: 'background 0.1s',
    },
    rowSelected: {
        background: '#EBF4FF',
    },
    rowDisabled: {
        background: '#F8F9FA',
        color: '#9aa0a6',
        cursor: 'not-allowed',
    },
    surveyName: {
        fontWeight: 600,
        color: '#1F2937',
    },
    fileName: {
        display: 'inline-block',
        maxWidth: 200,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        color: '#3C4043',
    },
    archivedName: {
        color: '#1F2937',
        fontWeight: 500,
    },
    archivedDate: {
        color: '#5F6368',
    },
    statusReady: {
        background: '#ECFDF5',
        color: '#065F46',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
    },
    statusBlocked: {
        background: '#FEF2F2',
        color: '#991B1B',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
    },
    scopeBadge: {
        background: '#F1F3F4',
        color: '#3C4043',
        fontSize: 10,
        fontWeight: 500,
        padding: '2px 8px',
        borderRadius: 10,
        textTransform: 'capitalize',
    },
    idChip: {
        fontFamily: 'monospace',
        fontSize: 11,
        color: '#1967D2',
    },
    notesCell: {
        display: 'inline-block',
        maxWidth: 240,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
    },
    notesEmpty: {
        color: '#BDC1C6',
    },
    placeholder: {
        padding: 32,
        textAlign: 'center',
        color: '#5F6368',
        fontSize: 13,
        lineHeight: 1.5,
    },
    modalIntro: {
        fontSize: 12,
        color: '#3C4043',
        marginBottom: 12,
        lineHeight: 1.4,
    },
    notesTextarea: {
        width: '100%',
        padding: 8,
        fontSize: 13,
        borderRadius: 6,
        border: '1px solid #DADCE0',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
        marginTop: 12,
    },
};

const modalStyles = {
    overlay: {
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
    },
    dialog: {
        background: '#ffffff',
        borderRadius: 8,
        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        maxWidth: '92%',
        maxHeight: '90%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        borderBottom: '1px solid #E8EAED',
        background: 'var(--color-bg-base)',
    },
    title: {
        fontWeight: 600,
        fontSize: 13,
        color: '#1F2937',
    },
    close: {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: '#5F6368',
        fontSize: 14,
        padding: 4,
    },
    body: {
        padding: '14px 16px',
        overflow: 'auto',
    },
};
